/** (파트너 × 품목) 조합의 미등록 단가를 인라인 등록.
 *  PO 단가로 기본값 채우고, 확인/수정 후 저장 → 발주일자별 PartnerPrice POST.
 *  유효기간 종료일은 각 발주일이 속한 달의 말일로 설정.
 */
import { useEffect, useRef, useState } from 'react'
import { CheckCircle2, Save } from 'lucide-react'

import { api } from '@/api/client'
import { useToast } from '@/stores/toast'
import { cn } from '@/lib/utils'

/** 주어진 날짜(YYYY-MM-DD)가 속한 달의 1일. 단가 유효기간 시작일 기본값용. */
function monthStartOf(iso: string): string {
  const d = new Date(iso + 'T00:00:00')
  if (isNaN(d.getTime())) return ''
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-01`
}

/** 주어진 날짜(YYYY-MM-DD)가 속한 달의 말일. 단가 유효기간 종료일용. */
function monthEndOf(iso: string): string {
  const d = new Date(iso + 'T00:00:00')
  if (isNaN(d.getTime())) return ''
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${last.getFullYear()}-${p(last.getMonth() + 1)}-${p(last.getDate())}`
}

export interface MissingPriceRow {
  partner: string
  partner_id: number | null
  item: string
  item_id: number | null
  matched_item_name: string
  dates: string[]
  po_price: string
}

interface Props {
  entries: MissingPriceRow[]
  /** 상위 스코프로 저장 (없으면 파트너 스코프 fallback — 하위 호환용). */
  groupId?: number | null
  /** 저장 성공 시 콜백. groupId 있으면 itemName 만 유의미 (모든 파트너 라인 갱신). */
  onSaved: (partnerName: string, itemName: string) => void
}

export function MissingPricesMapper({ entries, groupId, onSaved }: Props) {
  const push = useToast((s) => s.push)
  const [prices, setPrices] = useState<Record<string, string>>({})
  const [saved, setSaved] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState<Set<string>>(new Set())
  // 적용기간(전 행 일괄) — 기본: 시작일=발주일 월의 1일, 종료일=그 달 말일. 직접 수정 가능.
  const [effFrom, setEffFrom] = useState('')
  const [effTo, setEffTo] = useState('')
  useEffect(() => {
    if (effFrom) return   // 사용자가 이미 정했으면 덮어쓰지 않음
    const first = entries.flatMap((e) => e.dates).filter(Boolean).sort()[0]
    if (first) { setEffFrom(monthStartOf(first)); setEffTo(monthEndOf(first)) }
  }, [entries, effFrom])

  function keyOf(pn: string, item: string) { return `${pn}||${item}` }

  const tableRef = useRef<HTMLTableElement>(null)
  const saveAllBtnRef = useRef<HTMLButtonElement>(null)
  const savedRef = useRef(saved); savedRef.current = saved

  function focusRowPrice(idx: number) {
    requestAnimationFrame(() => {
      const input = tableRef.current?.querySelector<HTMLInputElement>(
        `tr[data-row-idx="${idx}"] input[type="number"]`,
      )
      if (input) { input.focus(); input.select() }
    })
  }
  function focusNextTodo(fromIdx: number = -1) {
    requestAnimationFrame(() => {
      for (let i = fromIdx + 1; i < entries.length; i++) {
        if (!savedRef.current.has(keyOf(entries[i].partner, entries[i].item))) return focusRowPrice(i)
      }
      for (let i = 0; i < entries.length; i++) {
        if (!savedRef.current.has(keyOf(entries[i].partner, entries[i].item))) return focusRowPrice(i)
      }
      saveAllBtnRef.current?.focus()
    })
  }

  useEffect(() => {
    if (entries.length > 0) focusNextTodo(-1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries.length])

  function priceFor(e: MissingPriceRow) {
    const k = keyOf(e.partner, e.item)
    return prices[k] ?? e.po_price
  }

  // 같은 item 을 group 스코프로 이미 저장했는지 추적 (dedup).
  const savedItemIds = useRef(new Set<number>())

  async function saveOne(e: MissingPriceRow) {
    const k = keyOf(e.partner, e.item)
    const priceStr = priceFor(e)
    if (!e.item_id) { push(`품목 '${e.item}' id 미상 - 매핑 저장 후 재시도`, 'warn'); return }
    const priceNum = Number(priceStr)
    if (!isFinite(priceNum) || priceNum <= 0) { push('유효한 단가 필요', 'warn'); return }
    if (!effFrom) { push('적용 시작일을 입력하세요', 'warn'); return }

    setBusy((cur) => new Set([...cur, k]))
    try {
      const useGroup = !!groupId
      if (!useGroup && !e.partner_id) {
        push(`파트너 '${e.partner}' id 미상 - 저장 불가`, 'warn')
        setBusy((cur) => { const next = new Set(cur); next.delete(k); return next })
        return
      }
      // group 스코프: 같은 item 이미 저장한 batch 내 request 중복 skip.
      if (useGroup && savedItemIds.current.has(e.item_id)) {
        // 다른 파트너 entry 였지만 이미 group 에 저장됨 — UI 만 완료로 마크.
        setSaved((cur) => new Set([...cur, k]))
        onSaved(e.partner, e.item)
        setBusy((cur) => { const next = new Set(cur); next.delete(k); return next })
        return
      }
      try {
        await api('/api/prices', {
          method: 'POST',
          body: {
            ...(useGroup
              ? { group: groupId, partner: null }
              : { partner: e.partner_id, group: null }),
            item: e.item_id,
            sale_price: priceStr,
            effective_from: effFrom,          // 상단에서 지정한 적용 시작일
            effective_to: effTo || null,      // 종료일(비우면 무기한)
            memo: '발주서 변환 인라인 등록' + (useGroup ? ' (상위 스코프)' : ''),
            is_active: true,
          },
        })
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err)
        if (!/uniq|unique/i.test(msg)) throw err
      }
      if (useGroup && e.item_id) savedItemIds.current.add(e.item_id)
      setSaved((cur) => new Set([...cur, k]))
      onSaved(e.partner, e.item)
      const scope = useGroup ? '(상위)' : ''
      push(`단가 저장 ${scope}: ${e.item} = ${priceStr}`, 'success')
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      push(`단가 저장 실패 (${e.item}): ${msg}`, 'error', 6000)
    } finally {
      setBusy((cur) => { const next = new Set(cur); next.delete(k); return next })
    }
  }

  async function saveAll() {
    const pending = entries.filter((e) => !saved.has(keyOf(e.partner, e.item)))
    for (const e of pending) {
      // eslint-disable-next-line no-await-in-loop
      await saveOne(e)
    }
  }

  function onTableKeyDown(ev: React.KeyboardEvent) {
    if (ev.key === 'Enter' && (ev.ctrlKey || ev.metaKey)) {
      ev.preventDefault()
      if (entries.filter(e => !saved.has(keyOf(e.partner, e.item))).length > 0) saveAll()
      return
    }
    const el = ev.target instanceof HTMLElement ? ev.target : null
    if (!el || !tableRef.current?.contains(el)) return
    const tr = el.closest<HTMLElement>('[data-row-idx]')
    const idxStr = tr?.getAttribute('data-row-idx')
    if (!idxStr) return
    const idx = parseInt(idxStr, 10)

    // input 안에서만 Arrow 처리 (number input 자체가 ↑↓ 로 값 증감하는 걸 override)
    if (ev.key === 'ArrowDown') {
      ev.preventDefault()
      if (idx + 1 < entries.length) focusRowPrice(idx + 1)
      else saveAllBtnRef.current?.focus()
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault()
      if (idx > 0) focusRowPrice(idx - 1)
    } else if (ev.key === 'Enter' && !ev.ctrlKey && !ev.metaKey) {
      ev.preventDefault()
      const e = entries[idx]
      if (!saved.has(keyOf(e.partner, e.item))) {
        saveOne(e).then(() => focusNextTodo(idx))
      } else {
        focusNextTodo(idx)
      }
    }
  }

  const remainingCount = entries.filter((e) => !saved.has(keyOf(e.partner, e.item))).length

  return (
    <div className="mx-4 mt-3 rounded-lg bg-white border border-amber-300 p-3">
      <div className="flex items-center gap-3 mb-2 flex-wrap">
        <div className="text-xs font-semibold text-amber-800">
          💰 거래처별 단가 미등록 ({remainingCount}조합)
        </div>
        <span className="text-xs text-slate-500">
          PO 단가로 기본값 채워짐. 아래 적용기간·단가 확인 후 저장 → 거래처별 단가(PartnerPrice) 등록.
        </span>
        {/* 적용기간(전 행 일괄) — 기본 발주일~그 달 말일, 직접 수정 가능 */}
        <div className="flex items-center gap-1.5 text-xs">
          <span className="text-slate-500 font-medium">적용</span>
          <input type="date" value={effFrom} onChange={(e) => setEffFrom(e.target.value)}
                 title="적용 시작일"
                 className="px-2 py-1 border border-slate-300 rounded text-xs tabular-nums bg-white focus:border-amber-500 focus:ring-1 focus:ring-amber-100 outline-none"/>
          <span className="text-slate-400">~</span>
          <input type="date" value={effTo} onChange={(e) => setEffTo(e.target.value)}
                 title="적용 종료일 (비우면 무기한)"
                 className="px-2 py-1 border border-slate-300 rounded text-xs tabular-nums bg-white focus:border-amber-500 focus:ring-1 focus:ring-amber-100 outline-none"/>
          {effTo && (
            <button onClick={() => setEffTo('')} title="종료일 비움(무기한)"
                    className="text-slate-400 hover:text-rose-500 px-0.5 text-sm leading-none">✕</button>
          )}
        </div>
        <div className="flex-1"/>
        <button
          ref={saveAllBtnRef}
          onClick={saveAll}
          disabled={remainingCount === 0}
          className={cn(
            'text-xs font-semibold rounded-lg px-3 py-1.5 inline-flex items-center gap-1 focus:ring-2 focus:ring-amber-300 focus:outline-none',
            remainingCount === 0
              ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
              : 'bg-amber-600 hover:bg-amber-700 text-white',
          )}>
          <Save className="w-3.5 h-3.5"/> 전체 저장 ({remainingCount})
        </button>
      </div>
      <div className="text-[10px] text-slate-500 mb-1">
        단축키: <kbd className="px-1 py-0.5 bg-slate-100 border border-slate-300 rounded font-mono">↑↓</kbd> 행 이동 ·
        <kbd className="px-1 py-0.5 bg-slate-100 border border-slate-300 rounded font-mono ml-1">Enter</kbd> 저장 후 다음 ·
        <kbd className="px-1 py-0.5 bg-slate-100 border border-slate-300 rounded font-mono ml-1">Ctrl+Enter</kbd> 전체 저장
      </div>

      <div className="border border-slate-200 rounded overflow-hidden max-h-80 overflow-y-auto">
        <table ref={tableRef} onKeyDown={onTableKeyDown} className="w-full text-xs">
          <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 tracking-wide">
            <tr>
              <th className="px-3 py-2 text-left font-medium">파트너</th>
              <th className="px-3 py-2 text-left font-medium">품목</th>
              <th className="px-3 py-2 text-left font-medium w-32">발주일</th>
              <th className="px-3 py-2 text-right font-medium w-32">매출단가</th>
              <th className="px-3 py-2 text-center w-24">상태/저장</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e, idx) => {
              const k = keyOf(e.partner, e.item)
              const isSaved = saved.has(k)
              const isBusy = busy.has(k)
              const val = priceFor(e)
              return (
                <tr key={k} data-row-idx={idx} className={cn(
                  'border-t border-slate-100',
                  isSaved ? 'bg-emerald-50/40' : 'bg-white hover:bg-slate-50',
                )}>
                  <td className="px-3 py-1.5 text-slate-800 truncate max-w-[160px]">{e.partner}</td>
                  <td className="px-3 py-1.5 text-slate-800 truncate max-w-[200px]">{e.matched_item_name}</td>
                  <td className="px-3 py-1.5 text-slate-500 tabular-nums text-[11px]">{e.dates.join(', ')}</td>
                  <td className="px-3 py-1.5">
                    <input
                      type="number" min="0" step="1"
                      value={val}
                      disabled={isSaved}
                      onChange={(ev) => setPrices((cur) => ({ ...cur, [k]: ev.target.value }))}
                      className="w-full px-2 py-1 border border-slate-300 rounded text-right tabular-nums text-xs focus:border-amber-500 focus:ring-1 focus:ring-amber-100 outline-none disabled:bg-slate-50 disabled:text-slate-500"
                    />
                  </td>
                  <td className="px-3 py-1.5 text-center">
                    {isSaved ? (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 inline-flex items-center gap-0.5">
                        <CheckCircle2 className="w-3 h-3"/> 저장됨
                      </span>
                    ) : (
                      <button
                        onClick={() => saveOne(e)}
                        disabled={isBusy || !e.partner_id || !e.item_id}
                        className={cn(
                          'text-[11px] font-semibold rounded px-2 py-0.5',
                          (isBusy || !e.partner_id || !e.item_id)
                            ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                            : 'bg-amber-600 hover:bg-amber-700 text-white',
                        )}>
                        {isBusy ? '...' : '저장'}
                      </button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
